// Holds the UTM quartet from the URL the visitor actually landed on, so a
// consent decision taken later in the visit does not cost us the search
// keyword.
//
// THE BUG THIS EXISTS FOR (order #1046, 2026-08-25). Under
// `cookieless_mode: 'on_reject'` posthog-js disables its persistence *entirely*
// while the SDK is opted out, which is every visitor's state until they answer
// the banner. In dist/module.js the decision reads:
//
//   if ("always" === config.cookieless_mode) return true;
//   const optedOut = consent.isOptedOut();
//   return config.disable_persistence ||
//     (optedOut && !(!config.opt_out_persistence_by_default &&
//                    config.cookieless_mode !== "on_reject"));
//
// With our config that is `true`, so nothing is stored pre-consent — including
// `$initial_person_info`, the single object the flattened `$initial_*` names are
// derived from (`{r: document.referrer, u: location.href}`). It gets registered
// on the first capture *after* `opt_in_capturing()` re-enables the store, from
// whatever URL the visitor is on at that moment. So a visitor who accepts the
// banner after a client-side navigation persists:
//   `$initial_referring_domain` — still correct, `document.referrer` survives
//                                 same-document navigation
//   `$initial_utm_*`            — gone, the landing URL's query string left
//                                 with the navigation
// which is how #1046 reached Shopify as `_mk_entry_ref=www.google.com` with a
// `_gclid` and no `_mk_utm` at all: a paid Google click whose keyword nothing in
// our data could name. Six of the eight preceding Google orders did carry the
// quartet — the difference was purely where the banner got clicked.
//
// The fix is to read the quartet the moment the client boots, while the landing
// URL is still the current one, hold it in memory (memory is not device storage,
// so it needs no consent) and hand it to PostHog's store the instant opting in
// makes that store writable.
//
// Reusing PostHog's persistence rather than a `marka.*` key of our own is
// deliberate: the values inherit its consent gating, its lifetime, and its
// clearing on revoke (`applyConsent('denied')` → `set_config({persistence:
// 'memory'})`), so there is no second storage surface to account for in the
// privacy policy. They ride along on events as super properties too, which is
// how the keyword becomes queryable in PostHog and not just readable in the
// Shopify admin.
//
// THE HARD NAVIGATION. The snapshot lives in the document, so a full page load
// would drop it — and the cart is reached by a deliberate plain `<a href>`
// (Next 16 Activity, see `cart-href.ts`), which is precisely where the quartet
// is read at checkout. Writing it to sessionStorage first would be exactly the
// pre-consent device write this posture exists to avoid, so instead the
// undecided visitor's cart link carries it in the URL — see
// `appendLandingAttributionToHref`. The document hands it to the next document; nothing
// is stored on the device, so ePrivacy Art. 5(3) is never engaged.
//
// KNOWN LIMIT: only navigations we render are covered. A visitor who types a
// URL, uses a bookmark, or restores a tab before answering the banner still
// loses the quartet. Their referrer is already our own domain by then, so the
// attribution is degraded regardless.

import posthog from "posthog-js";

import { getConsent } from "@/features/consent/consent-storage";
import {
  REORDER_QR_CARRY_PARAM,
  formatReorderQrMarker,
  readReorderQrMarker,
  type ReorderQrMarker,
} from "@/integrations/print/reorder-qr-attribution";
import {
  packUtmAttribute,
  parseUtmAttribute,
  type TrackingUtmParts,
} from "@/integrations/shopify/tracking-attributes";

interface PosthogWithProperties {
  __loaded?: boolean;
  register_once?: (properties: Record<string, string>) => void;
  get_property?: (key: string) => unknown;
}

/**
 * Prefixed rather than `$initial_utm_*`-shaped on purpose: these are our
 * record, written by us, and must not be mistaken for (or collide with) the
 * SDK's own first-touch properties.
 */
export const LANDING_UTM_PROPERTY_KEYS = {
  source: "mk_landing_utm_source",
  medium: "mk_landing_utm_medium",
  campaign: "mk_landing_utm_campaign",
  term: "mk_landing_utm_term",
} as const;

/**
 * The parameter our own cart link carries the quartet in. Deliberately NOT
 * `utm_*`: PostHog reads those off every pageview, so propagating them would
 * make `/cart` register as a campaign landing and corrupt session-source
 * reporting — fixing attribution in one place by breaking it in another. An
 * unrecognised key is inert to every analytics tool, and collides with neither
 * design-parameter registry (`DESIGN_URL_KEYS`, `COMPANY_DESIGN_URL_KEYS`).
 *
 * The value is `packUtmAttribute`'s output, so the URL form and the Shopify
 * attribute form are the same string, parsed by the same tested pair.
 */
export const LANDING_UTM_CARRY_PARAM = "mk_lu";

const UTM_QUERY_PARAMETERS = {
  source: "utm_source",
  medium: "utm_medium",
  campaign: "utm_campaign",
  term: "utm_term",
} as const;

type UtmField = keyof typeof UTM_QUERY_PARAMETERS;

const UTM_FIELDS = Object.keys(UTM_QUERY_PARAMETERS) as UtmField[];

let landingSnapshot: TrackingUtmParts = {};

// The reorder-QR marker rides the same document-to-document chain as the
// quartet, but is carried in every consent state: it is attached to the order
// regardless of consent (it names our order, not the device), and like the
// quartet it lives only in memory and the URL — never on the device.
let reorderQrSnapshot: ReorderQrMarker | null = null;

export function __resetLandingUtmSnapshotForTests(): void {
  landingSnapshot = {};
  reorderQrSnapshot = null;
}

/**
 * Reads the campaign parameters off the current URL. Call once per document,
 * as early in the client's life as there is a client — see
 * `instrumentation-client.ts`.
 */
export function snapshotLandingUtm(): void {
  if (typeof window === "undefined") {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const next: TrackingUtmParts = {};

  for (const field of UTM_FIELDS) {
    const value = params.get(UTM_QUERY_PARAMETERS[field])?.trim();
    if (value) {
      next[field] = value;
    }
  }

  // No real campaign parameters, but this may be a document we navigated to
  // ourselves and handed the quartet to. Second, not first: a URL carrying both
  // is a genuine ad click landing on a link we built, and the live `utm_*` is
  // then the more current truth.
  if (Object.keys(next).length === 0) {
    const carried = parseUtmAttribute(params.get(LANDING_UTM_CARRY_PARAM));
    for (const field of UTM_FIELDS) {
      const value = carried[field];
      if (value) {
        next[field] = value;
      }
    }
  }

  // Only a URL that actually carries campaign parameters may set the snapshot.
  // The cart is reached by a deliberate plain `<a href>` (`CartPageContent`,
  // to bypass Next 16's Activity route preservation), and that page load must
  // not blank out what the landing page recorded.
  if (Object.keys(next).length > 0) {
    landingSnapshot = next;
  }

  // Same rule: only a URL that carries a marker may set it.
  reorderQrSnapshot = readReorderQrMarker(params) ?? reorderQrSnapshot;
}

/** The reorder-QR scan this visit began with, if any. */
export function readLandingReorderQr(): ReorderQrMarker | null {
  return reorderQrSnapshot;
}

/**
 * Copies the snapshot into PostHog's store. Must run *after*
 * `opt_in_capturing()`: that call is what lifts the opted-out persistence
 * block described above, and a `register_once` before it is discarded without
 * a word. `__tests__/landing-attribution.sdk.test.ts` pins that against the
 * real SDK, because nothing about the API surface hints at it.
 */
export function persistLandingUtm(): void {
  if (typeof window === "undefined") {
    return;
  }

  const ph = posthog as unknown as PosthogWithProperties;
  if (!ph.__loaded || typeof ph.register_once !== "function") {
    return;
  }

  const properties: Record<string, string> = {};
  for (const field of UTM_FIELDS) {
    const value = landingSnapshot[field];
    if (value) {
      properties[LANDING_UTM_PROPERTY_KEYS[field]] = value;
    }
  }

  if (Object.keys(properties).length === 0) {
    return;
  }

  try {
    // `register_once`, so the campaign that started the journey wins over a
    // later ad click in the same browser — the same first-touch rule the SDK
    // applies to `$initial_*`, and the rule the rest of the attribution chain
    // assumes.
    ph.register_once(properties);
  } catch {
    // Deliberately silent, and deliberately not fatal: the only caller is the
    // consent bridge, and losing a keyword must never cost a consent decision.
    // `instrumentation-client.ts` already reports SDK faults.
  }
}

function readPersistedProperty(ph: PosthogWithProperties, key: string): string | undefined {
  if (typeof ph.get_property !== "function") {
    return undefined;
  }

  try {
    const value = ph.get_property(key);
    return typeof value === "string" && value.trim() ? value.trim() : undefined;
  } catch {
    return undefined;
  }
}

/**
 * How this journey's campaign parameters looked at the landing page, whether
 * that page was this document or an earlier one in the same browser.
 *
 * Persisted before in-memory: when the two disagree, the persisted pair is the
 * *earlier* record — this document may well be a later visit than the one that
 * wrote it, and first touch is what attribution wants.
 */
export function readLandingUtm(): TrackingUtmParts {
  const ph = posthog as unknown as PosthogWithProperties;
  const parts: TrackingUtmParts = {};

  for (const field of UTM_FIELDS) {
    const value =
      readPersistedProperty(ph, LANDING_UTM_PROPERTY_KEYS[field]) ?? landingSnapshot[field];
    if (value) {
      parts[field] = value;
    }
  }

  return parts;
}

/**
 * Adds the landing attribution to one of our own hrefs that unloads this
 * document: the reorder-QR marker always (see `reorderQrSnapshot`), and the
 * quartet for the single case that needs it — a visitor who has not yet
 * answered the consent banner.
 *
 * The quartet is gated on `getConsent() === null` — undecided — and that is the
 * whole point rather than a caution:
 *
 *   granted   — `persistLandingUtm` has already put the quartet in PostHog's
 *               localStorage, which survives the navigation on its own. Adding
 *               it to the URL would be redundant noise in the address bar.
 *   denied    — the visitor asked not to be tracked, and
 *               `collectTrackingAttributes` returns nothing for them anyway.
 *               Carrying campaign data around their session regardless would be
 *               pointless at best.
 *   undecided — the store is disabled, so the URL is the only thing that
 *               crosses the document boundary. This is the #1046 visitor.
 *
 * Returns `href` untouched whenever there is nothing to carry, so callers can
 * use it unconditionally.
 */
export function appendLandingAttributionToHref(href: string): string {
  if (typeof window === "undefined") {
    return href;
  }

  const carried: Record<string, string> = {};
  if (reorderQrSnapshot) {
    carried[REORDER_QR_CARRY_PARAM] = formatReorderQrMarker(reorderQrSnapshot);
  }
  const packed = isConsentUndecided() ? packUtmAttribute(readLandingUtm()) : null;
  if (packed) {
    carried[LANDING_UTM_CARRY_PARAM] = packed;
  }

  if (Object.keys(carried).length === 0) {
    return href;
  }

  try {
    // Parsed rather than concatenated: the href may already carry a query
    // string or a fragment, and `?`-vs-`&` guesswork is how those get mangled.
    // Relative to the current origin, and only the path is returned, so this
    // stays incapable of retargeting a link at another host.
    const url = new URL(href, window.location.origin);
    for (const [key, value] of Object.entries(carried)) {
      url.searchParams.set(key, value);
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

function isConsentUndecided(): boolean {
  try {
    return getConsent() === null;
  } catch {
    // Reading consent touches cookies and localStorage, both of which can throw
    // outright in a locked-down browser. A navigation must never depend on it.
    return false;
  }
}
